<template>
  <section class="page-shell document-validator-page">
    <div class="page-heading">
      <div>
        <h1>Validar documento</h1>
        <p>Envie ou cole o documento antes de usar com cliente, processo ou assinatura.</p>
      </div>
    </div>

    <div class="document-validator-grid" :class="{ 'document-validator-grid--single': !result }">
      <section class="data-panel document-input-panel">
        <div class="panel-kicker">Controle de saída</div>
        <h2>Documento gerado</h2>

        <div class="form-grid">
          <v-select v-model="form.document_type" :items="documentTypes" label="Tipo de documento" outlined dense hide-details />
          <v-select v-model="form.intended_use" :items="intendedUses" label="Uso pretendido" outlined dense hide-details />
        </div>

        <div class="form-grid">
          <v-text-field v-model="form.client_name" label="Cliente" outlined dense hide-details />
          <v-text-field v-model="form.matter_reference" label="Caso/processo/contrato" outlined dense hide-details />
        </div>

        <div class="document-source-switch">
          <v-btn-toggle v-model="inputMode" mandatory dense>
            <v-btn value="file">
              <v-icon left small>mdi-file-upload-outline</v-icon>
              Arquivo
            </v-btn>
            <v-btn value="text">
              <v-icon left small>mdi-text-box-edit-outline</v-icon>
              Texto
            </v-btn>
          </v-btn-toggle>
        </div>

        <div
          v-if="inputMode === 'file'"
          class="document-dropzone"
          :class="{ 'document-dropzone--dragging': isDraggingFile, 'document-dropzone--filled': documentFile }"
          @click="$refs.documentFileInput.click()"
          @dragover.prevent="isDraggingFile = true"
          @dragleave.prevent="isDraggingFile = false"
          @drop.prevent="handleFileDrop"
        >
          <input
            ref="documentFileInput"
            type="file"
            accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            class="document-dropzone-input"
            @change="handleFileSelect"
          >
          <v-icon>{{ documentFile ? 'mdi-file-check-outline' : 'mdi-cloud-upload-outline' }}</v-icon>
          <div>
            <strong>{{ documentFile ? documentFile.name : 'Arraste o arquivo aqui' }}</strong>
            <p>{{ documentFile ? fileSize(documentFile.size) : 'PDF, DOCX ou TXT. Você também pode clicar para selecionar.' }}</p>
          </div>
          <v-btn v-if="documentFile" small text color="error" @click.stop="clearDocumentFile">
            Remover
          </v-btn>
        </div>

        <v-textarea
          v-else
          v-model="form.document_text"
          label="Cole aqui o documento"
          outlined
          rows="14"
          counter
          hide-details
          no-resize
        />

        <div class="question-list">
          <div class="question-title">Confirmações do responsável</div>
          <v-checkbox v-model="form.human_reviewed" label="Um advogado/responsável revisou o conteúdo" dense hide-details />
          <v-checkbox v-model="form.source_checked" label="Leis, artigos e jurisprudências foram conferidos em fonte oficial" dense hide-details />
          <v-checkbox v-model="form.client_data_present" label="O texto contém dados de cliente, contraparte ou processo" dense hide-details />
          <v-checkbox v-model="form.external_delivery" label="O documento será enviado para cliente, contraparte, tribunal ou terceiro" dense hide-details />
        </div>

        <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
        <v-btn block color="primary" height="44" :loading="loading" @click="requestValidationConfirmation">
          Validar documento · usa créditos
        </v-btn>
        <p class="validation-credit-helper">A cobrança final depende do tamanho e risco do documento.</p>
      </section>

      <section v-if="result" class="data-panel document-result-panel">
        <div class="decision-badge" :class="`decision-${result.decision.status}`">
          {{ result.decision.label }}
        </div>
        <h2>{{ result.decision.message }}</h2>
        <p>{{ result.decision.next_step }}</p>

        <div class="validation-summary">
          <article>
            <span>Risco</span>
            <strong>{{ riskLabel(result.decision.risk_level) }}</strong>
          </article>
          <article>
            <span>Pendências</span>
            <strong>{{ result.decision.failed_count }}</strong>
          </article>
          <article>
            <span>Palavras</span>
            <strong>{{ result.facts.word_count }}</strong>
          </article>
        </div>

        <div v-if="aiReviewVisible" class="ai-review-box">
          <div class="ai-review-title">
            <v-icon small>mdi-auto-fix</v-icon>
            Análise automatizada
          </div>
          <template v-if="result.ai_review.status === 'completed'">
            <p>{{ result.ai_review.review.executive_summary }}</p>
            <div v-if="legalSourceReview" class="legal-source-review">
              <div>
                <strong>Referências jurídicas</strong>
                <v-chip small :color="legalSourceStatus.color" text-color="white">
                  {{ legalSourceStatus.label }}
                </v-chip>
              </div>
              <p>{{ legalSourceReview.summary }}</p>
              <ul v-if="legalSourceReview.concrete_references && legalSourceReview.concrete_references.length">
                <li v-for="source in legalSourceReview.concrete_references" :key="`concrete-${source}`">
                  {{ source }}
                </li>
              </ul>
              <ul v-if="legalSourceReview.incomplete_references && legalSourceReview.incomplete_references.length">
                <li v-for="source in legalSourceReview.incomplete_references" :key="`incomplete-${source}`">
                  {{ source }}
                </li>
              </ul>
            </div>
            <div class="ai-review-columns">
              <div>
                <strong>Riscos</strong>
                <ul>
                  <li v-for="risk in result.ai_review.review.main_risks || []" :key="risk">{{ risk }}</li>
                </ul>
              </div>
              <div>
                <strong>Ações exigidas</strong>
                <ul>
                  <li v-for="action in result.ai_review.review.required_actions || []" :key="action">{{ action }}</li>
                </ul>
              </div>
            </div>
          </template>
          <p v-else>{{ result.ai_review.summary }}</p>
        </div>

        <h3>Checklist de validação</h3>
        <div class="validation-checks">
          <div v-for="check in result.checks" :key="check.name" class="validation-check" :class="`check-${check.status}`">
            <v-icon>{{ check.status === 'passed' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}</v-icon>
            <div>
              <strong>{{ check.name }}</strong>
              <p>{{ check.message }}</p>
            </div>
          </div>
        </div>

        <div class="result-actions">
          <v-alert v-if="reviewMessage" type="success" dense text>{{ reviewMessage }}</v-alert>
          <v-btn
            v-if="humanReviewRecommended"
            block
            color="primary"
            :loading="reviewLoading"
            @click="openHumanReviewDialog"
          >
            <v-icon left small>mdi-account-check-outline</v-icon>
            Enviar para revisão humana
          </v-btn>
        </div>
      </section>
    </div>

    <v-dialog v-model="reviewDialog" max-width="520">
      <v-card>
        <v-card-title>Enviar para revisão humana</v-card-title>
        <v-card-text>
          <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
          <v-select
            v-model="selectedReviewer"
            :items="reviewerOptions"
            :loading="reviewersLoading"
            label="Responsável pela revisão"
            outlined
            dense
            hide-details
            no-data-text="Nenhum funcionário ativo encontrado"
          />
          <p class="dialog-helper">
            O responsável selecionado ficará registrado na fila e nas evidências da decisão.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reviewDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="reviewLoading" @click="sendToHumanReview">Enviar para revisão</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="validationConfirmDialog" max-width="460">
      <v-card class="validation-confirm-dialog">
        <v-card-title>Validar documento?</v-card-title>
        <v-card-text>
          <p>Essa validação usa IA e pode consumir créditos do plano.</p>
          <div class="validation-credit-preview">
            <span>Estimativa</span>
            <strong>{{ validationCreditEstimateLabel }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="validationConfirmDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="loading" @click="confirmValidation">
            Validar agora
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'

const validatorDraftKey = 'complyops:document-validator-draft'
const reviewValidationKey = requestId => `complyops:review-validation:${requestId}`

const emptyForm = () => ({
  document_type: '',
  client_name: '',
  matter_reference: '',
  intended_use: 'Uso interno',
  document_text: '',
  human_reviewed: false,
  source_checked: false,
  client_data_present: false,
  external_delivery: false
})

export default {
  name: 'DocumentValidatorPage',
  data () {
    return {
      form: emptyForm(),
      result: null,
      loading: false,
      reviewLoading: false,
      error: '',
      reviewMessage: '',
      reviewDialog: false,
      validationConfirmDialog: false,
      reviewersLoading: false,
      employees: [],
      selectedReviewer: '',
      inputMode: 'file',
      documentFile: null,
      generatedDocumentId: null,
      isDraggingFile: false,
      documentTypes: ['Minuta', 'Minuta contratual', 'Petição', 'Parecer', 'E-mail para cliente', 'Resumo jurídico', 'Notificação extrajudicial', 'Outro'],
      intendedUses: ['Uso interno', 'Enviar para cliente', 'Enviar para contraparte', 'Protocolar em processo', 'Assinatura', 'Treinamento interno']
    }
  },
  computed: {
    aiReviewVisible () {
      return this.result && this.result.ai_review && this.result.ai_review.status !== 'not_configured'
    },
    humanReviewRecommended () {
      return this.result && this.result.decision && ['blocked', 'needs_adjustment', 'approved_with_notes'].includes(this.result.decision.status)
    },
    legalSourceReview () {
      return this.result &&
        this.result.ai_review &&
        this.result.ai_review.review &&
        this.result.ai_review.review.legal_source_review
    },
    legalSourceStatus () {
      const status = this.legalSourceReview ? this.legalSourceReview.status : ''
      return {
        verified_by_text: { label: 'Referências concretas', color: 'success' },
        needs_official_check: { label: 'Conferir fonte oficial', color: 'warning' },
        incomplete_or_placeholder: { label: 'Incompleto', color: 'error' },
        no_sources_found: { label: 'Sem referências', color: 'grey' }
      }[status] || { label: 'Conferir', color: 'warning' }
    },
    reviewerOptions () {
      return this.employees
        .filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
        .map(employee => ({
          text: `${employee.name || employee.email} · ${employee.role_name || 'Sem papel'}`,
          value: this.reviewerValue(employee)
        }))
    },
    validationCreditEstimate () {
      const characters = this.estimatedValidationCharacters()
      let credits = 1
      if (characters > 60000) credits = 20
      else if (characters > 40000) credits = 10
      else if (characters > 15000) credits = 5
      else if (characters > 5000) credits = 2
      return credits
    },
    validationCreditEstimateLabel () {
      if (this.inputMode === 'file' && this.documentFile) {
        return `a partir de ${this.validationCreditEstimate} ${this.validationCreditEstimate === 1 ? 'crédito' : 'créditos'}`
      }
      return `${this.validationCreditEstimate} ${this.validationCreditEstimate === 1 ? 'crédito' : 'créditos'}`
    }
  },
  created () {
    this.loadReviewers()
    this.loadGeneratedDraft()
  },
  methods: {
    loadGeneratedDraft () {
      const rawDraft = localStorage.getItem(validatorDraftKey)
      if (!rawDraft) return

      try {
        const draft = JSON.parse(rawDraft)
        this.form = {
          ...this.form,
          document_type: draft.document_type || this.form.document_type,
          client_name: draft.client_name || '',
          matter_reference: draft.matter_reference || '',
          intended_use: draft.intended_use || this.form.intended_use,
          document_text: draft.document_text || ''
        }
        this.generatedDocumentId = draft.generated_document_id || null
        this.inputMode = 'text'
        this.documentFile = null
      } catch (error) {
        // Ignora rascunhos inválidos para não travar o validador.
      } finally {
        localStorage.removeItem(validatorDraftKey)
      }
    },
    requestValidationConfirmation () {
      this.error = ''
      if (this.inputMode === 'file' && !this.documentFile) {
        this.error = 'Envie um arquivo antes de validar.'
        return
      }
      if (this.inputMode === 'text' && !String(this.form.document_text || '').trim()) {
        this.error = 'Cole o texto do documento antes de validar.'
        return
      }
      this.validationConfirmDialog = true
    },
    async confirmValidation () {
      this.validationConfirmDialog = false
      await this.validateDocument()
    },
    async validateDocument () {
      this.loading = true
      this.error = ''
      this.reviewMessage = ''
      this.result = null
      try {
        const payload = new FormData()

        Object.keys(this.form).forEach(key => {
          payload.append(key, this.form[key])
        })

        if (this.inputMode === 'file' && this.documentFile) {
          payload.append('document_file', this.documentFile)
        }

        const { data } = await api.post('/ai-document-validations/validate', payload)
        this.result = data
        await this.updateGeneratedDocumentValidation(data)
      } catch (error) {
        const data = error.response && error.response.data ? error.response.data : null
        this.error = data ? data.message : 'Não foi possível validar este documento.'
        if (data && data.upload) {
          // Ajuda a diagnosticar metadados de upload sem expor o conteúdo do documento.
          console.warn('Falha no upload do documento', data.upload)
        }
        if (data && data.extraction) {
          // Ajuda a diagnosticar extração de texto sem expor o conteúdo do documento.
          console.warn('Falha na extração do documento', data.extraction)
        }
        if (data && data.quota) {
          this.result = {
            decision: {
              status: 'blocked',
              label: 'Limite atingido',
              risk_level: 'critical',
              message: data.message,
              next_step: 'Solicite liberação ao admin, compre pacote adicional ou aguarde a renovação mensal.',
              failed_count: 0
            },
            facts: { word_count: 0 },
            checks: [],
            quota: data.quota
          }
        }
      } finally {
        this.loading = false
      }
    },
    async updateGeneratedDocumentValidation (validation) {
      if (!this.generatedDocumentId || !validation) return

      try {
        await api.put(`/generated-documents/${this.generatedDocumentId}`, {
          document_text: this.reviewDocumentText(),
          validation_snapshot: validation
        })
      } catch (error) {
        console.warn('Não foi possível vincular validação ao documento gerado.', error)
      }
    },
    riskLabel (risk) {
      return { low: 'Baixo', medium: 'Médio', high: 'Alto', critical: 'Crítico' }[risk] || risk
    },
    handleFileSelect (event) {
      const file = event.target.files && event.target.files[0]
      this.setDocumentFile(file)
    },
    handleFileDrop (event) {
      this.isDraggingFile = false
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      this.setDocumentFile(file)
    },
    setDocumentFile (file) {
      this.error = ''
      if (!file) return

      const allowedTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ]
      const allowedExtensions = /\.(pdf|docx|txt)$/i

      if (!allowedTypes.includes(file.type) && !allowedExtensions.test(file.name)) {
        this.error = 'Envie um arquivo PDF, DOCX ou TXT.'
        return
      }

      this.documentFile = file
    },
    clearDocumentFile () {
      this.documentFile = null
      if (this.$refs.documentFileInput) {
        this.$refs.documentFileInput.value = ''
      }
    },
    fileSize (size) {
      if (!size) return ''
      if (size < 1024 * 1024) {
        return `${Math.ceil(size / 1024)} KB`
      }
      return `${(size / (1024 * 1024)).toFixed(1)} MB`
    },
    estimatedValidationCharacters () {
      if (this.inputMode === 'file' && this.documentFile) {
        return Math.min(Math.ceil(Number(this.documentFile.size || 0) / 2), 80000)
      }
      return new Blob([String(this.form.document_text || '')]).size
    },
    async loadReviewers () {
      this.reviewersLoading = true
      try {
        const { data } = await api.get('/employees')
        this.employees = data
        this.selectedReviewer = this.defaultReviewer()
      } catch (error) {
        this.employees = []
      } finally {
        this.reviewersLoading = false
      }
    },
    defaultReviewer () {
      const active = this.employees.filter(employee => employee.access_status === 'active' && employee.user_status === 'active')
      const preferred = active.find(employee => ['legal', 'admin', 'tenant_owner', 'security_dpo'].includes(employee.role_slug))
      const reviewer = preferred || active[0]
      return reviewer ? this.reviewerValue(reviewer) : 'Jurídico'
    },
    reviewerValue (employee) {
      const name = employee.name || employee.email
      return employee.email ? `${name} <${employee.email}>` : name
    },
    async openHumanReviewDialog () {
      this.error = ''
      const documentText = String(this.reviewDocumentText() || '').trim()
      if (documentText.length < 40) {
        this.error = documentText
          ? 'O documento precisa ter pelo menos 40 caracteres para revisão humana.'
          : 'Informe ou envie o documento antes de enviar para revisão.'
        return
      }
      if (!this.employees.length && !this.reviewersLoading) {
        await this.loadReviewers()
      }
      this.selectedReviewer = this.selectedReviewer || this.defaultReviewer()
      this.reviewDialog = true
    },
    async sendToHumanReview () {
      if (!this.result) return

      this.reviewLoading = true
      this.error = ''
      this.reviewMessage = ''

      try {
        const failedChecks = (this.result.checks || [])
          .filter(check => check.status === 'failed')
          .map(check => `${check.name}: ${check.message}`)
          .join('\n')

        const payload = {
          tool_name: 'Documento para validação',
          vendor_name: 'Não informado',
          business_purpose: `${this.form.document_type || 'Documento'} para ${this.form.intended_use || 'uso interno'}. ${this.result.decision.message}`,
          data_categories: failedChecks || this.result.decision.next_step,
          legal_basis: 'Políticas internas de validação documental',
          risk_level: this.result.decision.risk_level || 'medium',
          uses_personal_data: Boolean(this.form.client_data_present),
          client_name: this.form.client_name,
          matter_reference: this.form.matter_reference,
          use_case_type: this.form.document_type || 'Documento jurídico',
          confidentiality_level: this.form.client_data_present ? 'confidential' : 'internal',
          expected_benefit: 'Liberar, ajustar ou bloquear documento antes do uso externo.',
          output_review_required: true,
          human_reviewer: this.selectedReviewer || 'Jurídico',
          controls: this.result.decision.next_step,
          document_text: this.reviewDocumentText(),
          generated_document_id: this.generatedDocumentId,
          submit_now: true
        }

        const formData = new FormData()
        Object.keys(payload).forEach(key => {
          if (payload[key] !== null && payload[key] !== undefined) {
            formData.append(key, payload[key])
          }
        })

        if (!payload.document_text && this.inputMode === 'file' && this.documentFile) {
          formData.append('document_file', this.documentFile)
        }

        const { data } = await api.post('/human-review-requests', formData)
        const requestId = this.createdRequestId(data)
        if (requestId) this.saveReviewValidation(requestId)
        await this.markGeneratedDocumentInReview(payload.document_text)
        this.reviewMessage = 'Documento enviado para a fila de revisão humana.'
        this.reviewDialog = false
        window.setTimeout(() => {
          this.$router.push({ name: 'requests' })
        }, 700)
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível enviar para revisão humana.'
      } finally {
        this.reviewLoading = false
      }
    },
    createdRequestId (data) {
      return data && (data.id || (data.data && data.data.id) || (data.$attributes && data.$attributes.id))
    },
    reviewDocumentText () {
      if (this.result && this.result.source_document && this.result.source_document.text) {
        return this.result.source_document.text
      }

      return this.form.document_text
    },
    async markGeneratedDocumentInReview (documentText) {
      if (!this.generatedDocumentId) return

      try {
        await api.put(`/generated-documents/${this.generatedDocumentId}`, {
          status: 'in_review',
          document_text: documentText || this.reviewDocumentText()
        })
      } catch (error) {
        console.warn('Não foi possível marcar o documento como em revisão.', error)
      }
    },
    saveReviewValidation (requestId) {
      if (!requestId || !this.result) return

      const validation = {
        created_at: new Date().toISOString(),
        decision: this.result.decision || null,
        facts: this.result.facts || null,
        checks: this.result.checks || [],
        ai_review: this.result.ai_review || null,
        source_document: {
          type: this.form.document_type || '',
          intended_use: this.form.intended_use || '',
          client_name: this.form.client_name || '',
          matter_reference: this.form.matter_reference || '',
          file_name: this.documentFile ? this.documentFile.name : '',
          input_mode: this.inputMode
        }
      }

      window.localStorage.setItem(reviewValidationKey(requestId), JSON.stringify(validation))
    }
  }
}
</script>

<style lang="scss" scoped>
.validation-credit-helper {
  margin: 8px 0 0;
  color: #667085;
  font-size: 12px;
  line-height: 1.35;
  text-align: center;
}

.validation-confirm-dialog .v-card__title {
  padding-bottom: 6px;
  font-size: 18px;
  font-weight: 850;
}

.validation-confirm-dialog p {
  margin: 0 0 12px;
  color: #45524f;
  font-size: 13px;
  line-height: 1.45;
}

.validation-credit-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #d8e2ff;
  background: #f6f9ff;
}

.validation-credit-preview span {
  color: #60717d;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.validation-credit-preview strong {
  color: #0f4efb;
  font-size: 14px;
  font-weight: 850;
}
</style>
